/**
 * Escapes HTML characters in the given text.
 */
export function htmlEscape(str: string) {
	if (!str) {
		return "";
	}

	return str.replace(/&/g, '&amp;')
		.replace(/>/g, '&gt;')
		.replace(/</g, '&lt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
		.replace(/`/g, '&#96;');
}

export function quoteEscape(str: string) {
	if (!str) {
		return "";
	}

	return str.replace(/"/g, "\\\"");
}

/**
 * Properly formats user-inputted text to display in HTML.
 */
export function userText(str: string) {
	return htmlEscape(str).replace(/\r?\n/g, "<br />");
}

/**
 * an HTML snippet that's escaped by default.
 * Use $${"data"} to unescape an input.
 */
export function $html(literalSections, ...substs) {
	let raw = literalSections.raw;
	let result = "";

	substs.forEach((subst, i) => {
		let lit = raw[i];
		if (Array.isArray(subst)) {
			subst = subst.join("");
		}

		if (lit.endsWith("$")) {
			lit = lit.slice(0, -1);
		} else {
			if (subst == null) {
				subst = "null";
			} else {
				subst = htmlEscape(subst.toString());
			}
		}

		result += htmlEscape(lit);
		result += subst;
	});

	result += htmlEscape(raw[raw.length - 1]);

	return result;
}

/**
 * An HTML snippet that's unescaped by default.
 * Use $${"data"} to escape an input.
 */
export function html(literalSections, ...substs) {
	let raw = literalSections.raw;
	let result = "";

	substs.forEach((subst, i) => {
		let lit = raw[i];
		if (Array.isArray(subst)) {
			subst = subst.join("");
		}

		if (lit.endsWith("$")) {
			if (subst == null) {
				subst = "null";
			} else {
				subst = htmlEscape(subst.toString());
			}

			lit = lit.slice(0, -1);
		}

		result += lit;
		result += subst;
	});

	result += raw[raw.length - 1];

	return result;
}

/**
 * Executes the given string or function a given number of times.
 */
export function times(n: number, method: Function | string, inc: number = 1) {
	let buffer = [];

	for (let i = 0; i < n; i += inc) {
		if (typeof method === "function") {
			buffer.push(method(i));
		} else {
			buffer.push(method);
		}
	}

	return buffer.join("");
}

export {times as $times};

interface Stringable {
	toString(): string;
}
type EachIterator<T> = (value: T, key: number) => Stringable;

/**
 * DEPRECATED -- use $each or $map instead!
 * Executes the function or string a given number of times.
 * Returns an array; use .join() to choose the joiner, which is ", " by default
 */
export function each<T extends {}>(collection: T[], method: EachIterator<T> | string);
export function each<T extends {}>(collection: T[], method: EachIterator<T> | string) {
	let buffer = [];
	let len = collection.length;

	for (let i = 0; i < len; i++) {
		if (typeof method === "function") {
			buffer.push(method(collection[i], i).toString());
		} else {
			buffer.push(method.toString());
		}
	}

	return buffer;
}

export function $map(collection: any[], method: EachIterator<any> | string): string[];
export function $map<T extends {}>(collection: T[], method: EachIterator<T> | string): string[];
export function $map<T extends {}>(collection: T[], method: EachIterator<T> | string): string[] {
	return each<T>(collection, method);
}

/**
 * The Armature replacement for Circular's `each`
 * Automatically joins the given strings with nothing between them.
 * Use `$map` if you want an array.
 */
export function $each<T extends {}>(collection: T[], method: EachIterator<T> | string): string;
export function $each<T extends {}>(collection: T[], method: EachIterator<T> | string): string {
	return $map<T>(collection, method).join("");
}

export function $with<T extends {}>(object: T, callback: (v: T) => string): string {
	return callback(object);
}

/**
 * Executes the function or string only if the condition given is truthy.
 */
export function iff(condition: any, method: Function | string, other?: Function | string) {
	if (condition) {
		if (typeof method === "function") {
			return method();
		} else if (method) {
			return method;
		} else {
			return "";
		}
	} else {
		if (typeof other === "function") {
			return other();
		} else if (other) {
			return other;
		} else {
			return "";
		}
	}
}

export {iff as $if};

// Prototype asynchronous template wrapper from ekma
export function $async(literalSections, ...substs) {
	let raw = literalSections.raw;

	let proms = [];

	substs.forEach(subst => {
		if (typeof subst === "object" && "then" in subst) {
			proms.push(subst);
		} else {
			proms.push(Promise.resolve(subst));
		}
	});

	return Promise.all(proms)
		.then((results) => {
			let result = "";

			results.forEach((subst, i) => {
				result += raw[i];
				result += subst;
			});

			result += raw[raw.length - 1];

			return result;
		});
}